import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  viewChild,
} from '@angular/core';
import { MailCheck, X } from 'lucide-angular';

import { AuthModalService } from '../../../../shared/services/auth-modal.service';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { LoginFormComponent } from '../login-form/login-form.component';
import { RegisterFormComponent } from '../register-form/register-form.component';
import { ForgotPasswordFormComponent } from '../forgot-password-form/forgot-password-form.component';

/**
 * Modal contenedor de auth. Se monta una única vez en el root layout
 * (`app.html`) y reacciona al estado de `AuthModalService`. No renderiza
 * nada mientras `isOpen()` es false, así el coste en páginas sin auth
 * abierto es cero.
 *
 * Gestiona: escape para cerrar, click en backdrop para cerrar, focus
 * inicial al primer elemento focusable, y wrap manual de Tab/Shift+Tab
 * para no perder el foco fuera del modal. El bloqueo del scroll del
 * body lo hace `AuthModalService` a través de un effect.
 */
@Component({
  selector: 'app-auth-modal',
  imports: [
    IconComponent,
    ButtonComponent,
    LoginFormComponent,
    RegisterFormComponent,
    ForgotPasswordFormComponent,
  ],
  templateUrl: './auth-modal.component.html',
  styleUrl: './auth-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onCloseKey()',
    '(document:keydown.tab)': 'onTab($event)',
  },
})
export class AuthModalComponent {
  protected readonly authModalService = inject(AuthModalService);
  protected readonly isOpen = this.authModalService.isOpen;
  protected readonly mode = this.authModalService.mode;
  protected readonly registeredEmail = this.authModalService.lastRegisteredEmail;

  protected readonly closeIcon = X;
  protected readonly mailCheckIcon = MailCheck;

  protected readonly dialogRef = viewChild<ElementRef<HTMLElement>>('dialog');

  constructor() {
    afterNextRender(() => this.focusFirstOnOpen());
  }

  private focusFirstOnOpen(): void {
    // Enfoca al primer elemento focusable cuando el modal aparece.
    // Se re-ejecuta cada vez que el host detecta cambios tras apertura.
    if (!this.isOpen()) return;
    const first = this.getFocusable()[0];
    first?.focus();
  }

  private getFocusable(): HTMLElement[] {
    const root = this.dialogRef()?.nativeElement;
    if (!root) return [];
    const nodes = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    return Array.from(nodes).filter(
      (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
    );
  }

  protected onTab(event: Event): void {
    if (!this.isOpen()) return;
    const keyboardEvent = event as KeyboardEvent;
    const focusables = this.getFocusable();
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (keyboardEvent.shiftKey) {
      if (active === first || !active || !this.dialogRef()?.nativeElement.contains(active)) {
        keyboardEvent.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        keyboardEvent.preventDefault();
        first.focus();
      }
    }
  }

  protected onCloseKey(): void {
    if (this.isOpen()) this.authModalService.close();
  }

  protected onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.authModalService.close();
    }
  }

  protected onCloseClick(): void {
    this.authModalService.close();
  }

  protected titleFor(): string {
    switch (this.mode()) {
      case 'login':
        return 'Iniciar sesión';
      case 'register':
        return 'Crear cuenta';
      case 'forgot':
        return 'Recuperar contraseña';
      case 'check-email':
        return 'Revisa tu correo';
    }
  }

  protected onCheckEmailDone(): void {
    this.authModalService.close();
  }

  protected switchToLogin(): void {
    this.authModalService.switchMode('login');
  }
}
